/**
 * Join Person Unit Service for Outlook Add-in
 * Handles person-unit relationship data fetching from DoNexus API
 * This service manages the relationships between contacts and units (apartments)
 */

import apiClient from '../utils/apiClient';

class JoinPersonUnitService {
  /**
   * Get all person-unit relationships with optional filters
   * @param {Object} filters - Query parameters for filtering
   * @param {string} filters.property_id - Filter by property ID
   * @param {string} filters.unit_id - Filter by unit ID
   * @param {string} filters.contact_id - Filter by contact ID
   * @param {string} filters.type - Filter by relationship type (e.g., "Eigentümer", "Mieter")
   * @param {boolean} filters.active - Filter by active status
   * @param {boolean} filters.enhanced - Request enhanced data with contact details
   * @param {boolean} filters.advisory_board - Filter by advisory board membership
   * @param {Object} options - Pagination options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 20)
   * @returns {Promise<Object>} Response with relationships data and pagination
   */
  async getAllJoinPersonUnits(filters = {}, options = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      queryParams.append('page', (options.page || 1).toString());
      queryParams.append('limit', (options.limit || 20).toString());

      // Add filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const queryString = queryParams.toString();
      const endpoint = `/join-person-unit?${queryString}`;

      const response = await apiClient.get(endpoint);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data.data || [],
          pagination: response.data.pagination || null,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to fetch person-unit relationships');
      }
    } catch (error) {
      console.error('Error fetching person-unit relationships:', error);
      throw error;
    }
  }

  /**
   * Get contacts for a specific property by role type
   * @param {string} propertyId - Property ID
   * @param {string} roleType - Role type filter: 'council' (Beiräte), 'owners' (Eigentümer), 'residents' (Mieter)
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Contacts with their unit relationships
   */
  async getContactsByPropertyRole(propertyId, roleType, options = {}) {
    try {
      const filters = {
        property_id: propertyId,
        active: true,
        enhanced: true // Get full contact details
      };

      // Map role types to API types
      if (roleType === 'council') {
        filters.advisory_board = true;
      } else if (roleType === 'owners') {
        filters.type = 'Eigentümer';
      } else if (roleType === 'residents') {
        filters.type = 'Mieter';
      }

      const response = await this.getAllJoinPersonUnits(filters, {
        page: options.page || 1,
        limit: options.limit || 100
      });

      if (response.success) {
        // Filter out entries without contacts (empty units)
        const contactsWithUnits = response.data.filter(
          relationship => relationship.contact_id && relationship.contact
        );

        return {
          success: true,
          data: contactsWithUnits,
          pagination: response.pagination,
          message: response.message
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching contacts by property role:', error);
      throw error;
    }
  }

  /**
   * Get unique contacts from person-unit relationships
   * Useful for extracting distinct contacts when multiple units may be involved
   * @param {Array} relationships - Array of person-unit relationships
   * @returns {Array} Array of unique contacts with their email addresses
   */
  extractUniqueContacts(relationships) {
    const contactMap = new Map();

    relationships.forEach(rel => {
      if (rel.contact_id && rel.contact) {
        const contact = rel.contact;
        const contactInfo = rel.contact_info || {};
        
        if (!contactMap.has(rel.contact_id)) {
          contactMap.set(rel.contact_id, {
            id: rel.contact_id,
            name: contactInfo.full_name || `${contact.name || ''} ${contact.surname || ''}`.trim() || 'Unknown',
            email: contactInfo.email || this.extractPrimaryEmail(contact.contact_details),
            phone: contactInfo.phone || this.extractPrimaryPhone(contact.contact_details),
            address: contactInfo.address || null,
            salutation: contact.salutation || null,
            units: []
          });
        }

        // Add unit information
        if (rel.unit) {
          contactMap.get(rel.contact_id).units.push({
            id: rel.unit_id,
            apartment_number: rel.unit.apartment_number,
            type: rel.unit.type,
            floor: rel.unit.floor,
            size: rel.unit.size,
            relationship_type: rel.type,
            is_advisory_board: rel.advisory_board
          });
        }
      }
    });

    return Array.from(contactMap.values());
  }

  /**
   * Extract primary email from contact details array
   * @param {Array} contactDetails - Array of contact detail objects
   * @returns {string|null} Primary email address or null
   */
  extractPrimaryEmail(contactDetails) {
    if (!Array.isArray(contactDetails)) return null;
    const emailDetail = contactDetails.find(
      detail => detail.type === 'email' && detail.label === 'primary'
    );
    return emailDetail ? emailDetail.value : null;
  }

  /**
   * Extract primary phone from contact details array
   * @param {Array} contactDetails - Array of contact detail objects
   * @returns {string|null} Primary phone number or null
   */
  extractPrimaryPhone(contactDetails) {
    if (!Array.isArray(contactDetails)) return null;
    const phoneDetail = contactDetails.find(
      detail => detail.type === 'phone'
    );
    return phoneDetail ? phoneDetail.value : null;
  }
}

// Create singleton instance
const joinPersonUnitService = new JoinPersonUnitService();

export default joinPersonUnitService;

